import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import firebase from "firebase/app";
import { auth, db } from "../FIREBASE/firebase";
import routeUrls from "../COMPONENTS/navbar/navLink";
import { usePageBackground } from "../COMPONENTS/usePageBackground";
import NumberInput from "../COMPONENTS/NumberInput";
import {
  Task, TodoList, TodoData, Priority, Status, ListKind, Template, Sharing,
  listFromDoc, sharedFields, normEmail, mergeRemote, Remote,
  blankTask, blankList, blankGroup, normalizeData, withPacked, packAll, packItems,
  listFromTemplate, templateFromList, BUILTIN_TEMPLATES,
  mapTask, removeTask, addChild, moveTask, placeTask, shiftTask, nestTask, findTask, progress, openCount,
  dueStatus, formatDue, STATUS_META, STATUS_ORDER, nextStatus, PRIORITY_LABEL, dueSummary, sortTasks, SortBy,
} from "../COMPONENTS/todo/todo";
import "../CSS/todo.scss";

interface Handlers {
  cycle: (id: string) => void;
  setStatus: (id: string, s: Status) => void;
  setPrio: (id: string, p: Priority) => void;
  setDue: (id: string, d: string) => void;
  del: (id: string) => void;
  editingId: string | null;
  editText: string;
  setEditText: (v: string) => void;
  startEdit: (t: Task) => void;
  commitEdit: () => void;
  cancelEdit: () => void;
  addingSubFor: string | null;
  subText: string;
  setSubText: (v: string) => void;
  openAddSub: (id: string) => void;
  commitAddSub: () => void;
  cancelAddSub: () => void;
  moveTargets: TodoList[]; // lists a task can be moved to
  move: (id: string, listId: string) => void;
  setDragging: (id: string | null) => void;
  dragging: string | null;
  draggingGroup: boolean;
  dropAt: DropAt | null;
  setDropAt: (d: DropAt | null) => void;
  place: (to: DropAt) => void;
  groups: Task[];          // packing list groups an item can go into
  nest: (id: string, groupId: string) => void;
  packGroup: (id: string, on: boolean) => void;
  sumOf: (id: string) => { items: number; packed: number };
  shift: (id: string, by: -1 | 1) => void;
  setQty: (id: string, n: number) => void;
  setPacked: (id: string, n: number) => void;
  setNote: (id: string, note: string) => void;
  openId: string;          // row whose ⋯ menu is open
  setOpenId: (id: string) => void;
  sorted: boolean;         // shown sorted, so dragging and up/down make no sense
}

interface DropAt { id: string; after: boolean; inside?: boolean }

// Drag a row onto another list in the sidebar, or onto another row to put it
// before or after it (upper or lower half). An item dropped on a group heading
// goes into that group.
const dragProps = (task: Task, h: Handlers) => ({
  draggable: h.editingId !== task.id && !h.sorted,
  onDragStart: (e: React.DragEvent) => {
    e.stopPropagation();
    e.dataTransfer.setData("text/plain", task.text);
    e.dataTransfer.effectAllowed = "move";
    h.setDragging(task.id);
  },
  onDragEnd: () => h.setDragging(null),
  onDragOver: (e: React.DragEvent<HTMLElement>) => {
    if (!h.dragging || h.dragging === task.id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const r = e.currentTarget.getBoundingClientRect();
    const after = e.clientY > r.top + r.height / 2;
    const inside = task.group && !h.draggingGroup;
    if (h.dropAt?.id !== task.id || h.dropAt.after !== after || !!h.dropAt.inside !== inside) h.setDropAt({ id: task.id, after, inside });
  },
  onDrop: (e: React.DragEvent) => {
    e.preventDefault();
    if (h.dropAt) h.place(h.dropAt);
    h.setDragging(null);
  },
});

const dropClass = (task: Task, h: Handlers) =>
  h.dropAt?.id !== task.id ? ""
  : h.dropAt.inside ? "todo__item--drop-in"
  : h.dropAt.after ? "todo__item--drop-after" : "todo__item--drop-before";

// Up/down within the list (for touch screens, where dragging does not work),
// into another group, or over to another list
const MoveSelect = ({ task, h, parent = "" }: { task: Task; h: Handlers; parent?: string }) => (
  <select
    className="todo__move"
    value=""
    onChange={(e) => {
      const v = e.target.value;
      if (v === "up" || v === "down") h.shift(task.id, v === "up" ? -1 : 1);
      else if (v.startsWith("g:")) h.nest(task.id, v.slice(2));
      else if (v) h.move(task.id, v);
    }}
    title="Flytta upp, ner eller till en annan lista"
    aria-label="Flytta"
  >
    <option value="">Flytta…</option>
    {!h.sorted && <option value="up">↑ Upp</option>}
    {!h.sorted && <option value="down">↓ Ner</option>}
    {!task.group && h.groups.length > 0 && (
      <optgroup label="Till grupp">
        {h.groups.filter((g) => g.id !== parent).map((g) => <option key={g.id} value={`g:${g.id}`}>{g.text}</option>)}
        {parent && <option value="g:">Ingen grupp</option>}
      </optgroup>
    )}
    {h.moveTargets.length > 0 && (
      <optgroup label="Till lista">
        {h.moveTargets.map((l) => <option key={l.id} value={l.id}>{l.icon} {l.name}</option>)}
      </optgroup>
    )}
  </select>
);

// What you rarely do to a row is kept out of sight until ⋯ is pressed, so the
// list itself only shows what matters at a glance
const MoreBtn = ({ task, h }: { task: Task; h: Handlers }) => (
  <button
    className="todo__more"
    onClick={() => h.setOpenId(h.openId === task.id ? "" : task.id)}
    aria-expanded={h.openId === task.id}
    aria-label={`Fler val för ${task.text}`}
    title="Ändra, flytta eller ta bort"
  >⋯</button>
);

function Actions({ task, h, parent, onDelete, indent = 0, children }: {
  task: Task; h: Handlers; parent?: string; onDelete?: () => void; indent?: number; children?: React.ReactNode;
}) {
  if (h.openId !== task.id) return null;
  return (
    <div className="todo__actions" style={indent ? { marginLeft: indent } : undefined}>
      {children}
      <MoveSelect task={task} h={h} parent={parent} />
      <button className="todo__act" onClick={() => { h.setOpenId(""); h.startEdit(task); }}>Byt namn</button>
      <button className="todo__act todo__act--del" onClick={onDelete ?? (() => h.del(task.id))}>Ta bort</button>
    </div>
  );
}

const ICONS = ["✅", "🧳", "🏠", "🛒", "💼", "🎯", "🎁", "🏋️", "📚", "🔧", "🌱", "📝", "📌", "📺", "🎬"];

function TaskNode({ task, depth, h }: { task: Task; depth: number; h: Handlers }) {
  const p = progress(task);
  const ds = dueStatus(task.due);
  const meta = STATUS_META[task.status];
  const editRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (h.editingId === task.id) editRef.current?.focus(); }, [h.editingId, task.id]);

  return (
    <li className="todo__node">
      <div
        className={`todo__item todo__item--${task.status} todo__item--p-${task.priority} ${dropClass(task, h)}`}
        style={{ marginLeft: depth * 22 }}
        {...dragProps(task, h)}
      >
        <button
          className={`todo__status todo__status--${task.status}`}
          onClick={() => h.cycle(task.id)}
          title={`${meta.label} — klicka för att växla`}
        >{meta.glyph}</button>

        {h.editingId === task.id ? (
          <input
            ref={editRef}
            className="todo__edit"
            value={h.editText}
            onChange={(e) => h.setEditText(e.target.value)}
            onBlur={h.commitEdit}
            onKeyDown={(e) => { if (e.key === "Enter") h.commitEdit(); if (e.key === "Escape") h.cancelEdit(); }}
          />
        ) : (
          <span className="todo__text" onDoubleClick={() => h.startEdit(task)} title="Dubbelklicka för att ändra">{task.text}</span>
        )}

        {(p.total > 0 || task.due || task.priority === "high") && (
          <span className="todo__meta">
            {task.priority === "high" && <span className="todo__flag">Viktig</span>}
            {p.total > 0 && (
              <span className="todo__prog" title={`${p.done} av ${p.total} klara`}>
                <span className="todo__prog-bar"><span className="todo__prog-fill" style={{ width: `${Math.round((p.done / p.total) * 100)}%` }} /></span>
                <span className="todo__prog-num">{p.done}/{p.total}</span>
              </span>
            )}
            {task.due && <span className={`todo__due todo__due--${ds}`}>{formatDue(task.due)}</span>}
          </span>
        )}
        <MoreBtn task={task} h={h} />
      </div>

      <Actions task={task} h={h} indent={depth * 22}>
        <label className="todo__act-field">Status
          <select value={task.status} onChange={(e) => h.setStatus(task.id, e.target.value as Status)}>
            {STATUS_ORDER.map((st) => <option key={st} value={st}>{STATUS_META[st].label}</option>)}
          </select>
        </label>
        <label className="todo__act-field">Prioritet
          <select value={task.priority} onChange={(e) => h.setPrio(task.id, e.target.value as Priority)}>
            {(["high", "normal", "low"] as Priority[]).map((pr) => <option key={pr} value={pr}>{PRIORITY_LABEL[pr]}</option>)}
          </select>
        </label>
        <label className="todo__act-field">Deadline
          <input type="date" value={task.due} onChange={(e) => h.setDue(task.id, e.target.value)} />
        </label>
        <button className="todo__act" onClick={() => { h.setOpenId(""); h.openAddSub(task.id); }}>＋ Deluppgift</button>
      </Actions>

      {(task.children.length > 0 || h.addingSubFor === task.id) && (
        <ul className="todo__children">
          {task.children.map((c) => <TaskNode key={c.id} task={c} depth={depth + 1} h={h} />)}
          {h.addingSubFor === task.id && (
            <li className="todo__node">
              <div className="todo__subinput" style={{ marginLeft: (depth + 1) * 22 }}>
                <input
                  autoFocus
                  className="todo__edit"
                  placeholder="Deluppgift…"
                  value={h.subText}
                  onChange={(e) => h.setSubText(e.target.value)}
                  onBlur={h.commitAddSub}
                  onKeyDown={(e) => { if (e.key === "Enter") h.commitAddSub(); if (e.key === "Escape") h.cancelAddSub(); }}
                />
              </div>
            </li>
          )}
        </ul>
      )}
    </li>
  );
}

// One thing to pack: tick it to pack them all, or count them in one at a time
function PackNode({ task, h, parent = "" }: { task: Task; h: Handlers; parent?: string }) {
  const all = task.packed >= task.qty;
  const editRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (h.editingId === task.id) editRef.current?.focus(); }, [h.editingId, task.id]);
  return (
    <li className="todo__node">
      <div
        className={`todo__item todo__item--pack ${all ? "todo__item--done" : ""} ${dropClass(task, h)}`}
        {...dragProps(task, h)}
      >
        <button
          className={`todo__status todo__status--${all ? "done" : task.packed > 0 ? "ongoing" : "todo"}`}
          onClick={() => h.setPacked(task.id, all ? 0 : task.qty)}
          title={all ? "Packad — klicka för att packa upp" : "Klicka när allt är packat"}
          aria-label={all ? `Packa upp ${task.text}` : `Packa ${task.text}`}
        >{all ? "✓" : task.packed > 0 ? "◐" : ""}</button>

        {h.editingId === task.id ? (
          <input
            ref={editRef}
            className="todo__edit"
            value={h.editText}
            onChange={(e) => h.setEditText(e.target.value)}
            onBlur={h.commitEdit}
            onKeyDown={(e) => { if (e.key === "Enter") h.commitEdit(); if (e.key === "Escape") h.cancelEdit(); }}
          />
        ) : (
          <span className="todo__text" onDoubleClick={() => h.startEdit(task)} title="Dubbelklicka för att ändra">{task.text}</span>
        )}

        {task.qty > 1 && (
          <span className="todo__count-pack" aria-label={`${task.packed} av ${task.qty} packade`}>
            <button onClick={() => h.setPacked(task.id, task.packed - 1)} disabled={task.packed <= 0} aria-label="En färre packad">−</button>
            <b>{task.packed}/{task.qty}</b>
            <button onClick={() => h.setPacked(task.id, task.packed + 1)} disabled={all} aria-label="En till packad">+</button>
          </span>
        )}
        <MoreBtn task={task} h={h} />
      </div>
      <Actions task={task} h={h} parent={parent}>
        <label className="todo__act-field todo__qty">Antal
          <NumberInput min={1} max={99} integer inputMode="numeric" value={task.qty} onChange={(n) => h.setQty(task.id, n)} />
          st
        </label>
      </Actions>
    </li>
  );
}

// A heading in a packing list with its related items under it. Ticking it
// packs (or unpacks) everything in the group.
function PackGroup({ task, h }: { task: Task; h: Handlers }) {
  const [open, setOpen] = useState(true);
  const sum = h.sumOf(task.id);
  const all = sum.items > 0 && sum.packed >= sum.items;
  const editRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (h.editingId === task.id) { editRef.current?.focus(); editRef.current?.select(); } }, [h.editingId, task.id]);
  const adding = h.addingSubFor === task.id;
  return (
    <li className="todo__node todo__group">
      <div className={`todo__item todo__group-head ${all ? "todo__group-head--done" : ""} ${dropClass(task, h)}`} {...dragProps(task, h)}>
        <button className="todo__fold" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-label={open ? `Fäll ihop ${task.text}` : `Fäll ut ${task.text}`}>
          {open ? "▾" : "▸"}
        </button>
        <button
          className={`todo__status todo__status--${all ? "done" : sum.packed > 0 ? "ongoing" : "todo"}`}
          onClick={() => h.packGroup(task.id, !all)}
          disabled={!sum.items}
          title={all ? "Allt i gruppen är packat — klicka för att packa upp" : "Packa allt i gruppen"}
          aria-label={all ? `Packa upp allt i ${task.text}` : `Packa allt i ${task.text}`}
        >{all ? "✓" : sum.packed > 0 ? "◐" : ""}</button>

        {h.editingId === task.id ? (
          <input
            ref={editRef}
            className="todo__edit"
            value={h.editText}
            onChange={(e) => h.setEditText(e.target.value)}
            onBlur={h.commitEdit}
            onKeyDown={(e) => { if (e.key === "Enter") h.commitEdit(); if (e.key === "Escape") h.cancelEdit(); }}
          />
        ) : (
          <span className="todo__text todo__group-name" onDoubleClick={() => h.startEdit(task)} title="Dubbelklicka för att byta namn">{task.text}</span>
        )}

        {sum.items > 0 && (
          <span className="todo__prog" title={`${sum.packed} av ${sum.items} saker packade`}>
            <span className="todo__prog-bar"><span className="todo__prog-fill" style={{ width: `${Math.round((sum.packed / sum.items) * 100)}%` }} /></span>
            <span className="todo__prog-num">{sum.packed}/{sum.items}</span>
          </span>
        )}
        <button className="todo__sub-add" onClick={() => { setOpen(true); h.openAddSub(task.id); }} title="Lägg till i gruppen" aria-label={`Lägg till i ${task.text}`}>＋</button>
        <MoreBtn task={task} h={h} />
      </div>
      <Actions
        task={task} h={h}
        onDelete={() => { if (!sum.items || window.confirm(`Ta bort gruppen "${task.text}" och allt i den?`)) h.del(task.id); }}
      />

      {open && (task.children.length > 0 || adding) && (
        <ul className="todo__children todo__group-items">
          {task.children.map((c) => <PackNode key={c.id} task={c} h={h} parent={task.id} />)}
          {adding && (
            <li className="todo__node">
              <div className="todo__subinput">
                <input
                  autoFocus
                  className="todo__edit"
                  placeholder={`Lägg till i ${task.text}…`}
                  value={h.subText}
                  onChange={(e) => h.setSubText(e.target.value)}
                  onBlur={h.commitAddSub}
                  onKeyDown={(e) => { if (e.key === "Enter") { h.commitAddSub(); h.openAddSub(task.id); } if (e.key === "Escape") h.cancelAddSub(); }}
                />
              </div>
            </li>
          )}
        </ul>
      )}
      {open && sum.items > 0 && task.children.length === 0 && !adding && <p className="todo__group-done">Allt packat ✓</p>}
    </li>
  );
}

// Something to remember: a name and an optional note under it. Nothing to
// tick off, so no status circle.
function NoteNode({ task, h }: { task: Task; h: Handlers }) {
  const [writing, setWriting] = useState(false);
  const [draft, setDraft] = useState("");
  const editRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (h.editingId === task.id) editRef.current?.focus(); }, [h.editingId, task.id]);
  const write = () => { setDraft(task.note); setWriting(true); };
  const save = () => { h.setNote(task.id, draft.trim()); setWriting(false); };
  return (
    <li className="todo__node">
      <div className={`todo__item todo__item--note ${dropClass(task, h)}`} {...dragProps(task, h)}>

        <div className="todo__note-body">
          {h.editingId === task.id ? (
            <input
              ref={editRef}
              className="todo__edit"
              value={h.editText}
              onChange={(e) => h.setEditText(e.target.value)}
              onBlur={h.commitEdit}
              onKeyDown={(e) => { if (e.key === "Enter") h.commitEdit(); if (e.key === "Escape") h.cancelEdit(); }}
            />
          ) : (
            <span className="todo__text" onDoubleClick={() => h.startEdit(task)} title="Dubbelklicka för att ändra">{task.text}</span>
          )}
          {writing ? (
            <textarea
              autoFocus
              className="todo__note-edit"
              rows={2}
              placeholder="Anteckning, t.ex. var den finns eller vem som tipsade…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={save}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); save(); } if (e.key === "Escape") setWriting(false); }}
            />
          ) : task.note && (
            <p className="todo__note" onClick={write} title="Klicka för att ändra">{task.note}</p>
          )}
        </div>

        <MoreBtn task={task} h={h} />
      </div>
      <Actions task={task} h={h}>
        <button className="todo__act" onClick={() => { h.setOpenId(""); write(); }}>✎ {task.note ? "Ändra anteckning" : "Anteckning"}</button>
      </Actions>
    </li>
  );
}

const KIND_LABEL: Record<ListKind, string> = { tasks: "Uppgifter", pack: "Packlista", notes: "Minneslista" };
const NEW_LIST: Record<ListKind, [string, string]> = { tasks: ["Ny lista", ""], pack: ["Ny packlista", "🧳"], notes: ["Ny minneslista", "📌"] };

// hide done/ignored tasks (keeping parents that still have visible children)
const filterTasks = (tasks: Task[], showDone: boolean): Task[] => {
  if (showDone) return tasks;
  const out: Task[] = [];
  tasks.forEach((t) => {
    const kids = filterTasks(t.children, showDone);
    const keepSelf = t.group || (t.status !== "done" && t.status !== "ignored");
    if (keepSelf || kids.length) out.push({ ...t, children: kids });
  });
  return out;
};

const sharedKey = (l: TodoList) => JSON.stringify(sharedFields(l));
const isShared = (l: TodoList, uid: string) => !!l.shared && (l.shared.owner !== uid || l.shared.sharedWith.length > 0);

export const TodoView = ({ uid }: { uid: string }) => {
  usePageBackground();
  const email = normEmail(auth.currentUser?.email ?? "");
  const [data, setData] = useState<TodoData>({ lists: [], activeListId: "", templates: [], order: [] });
  const [hydrated, setHydrated] = useState(false);
  const [text, setText] = useState("");
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState<Priority>("normal");
  const [qty, setQty] = useState(1);
  const [addTo, setAddTo] = useState(""); // packing list group new items go into
  const [picking, setPicking] = useState(false); // choosing what a new list starts from
  const [showDone, setShowDone] = useState(false);
  const [openId, setOpenId] = useState("");
  const [sortBy, setSortByState] = useState<SortBy>(() => {
    try { const v = localStorage.getItem("todo-sort"); return v === "due" || v === "prio" ? v : "own"; } catch { return "own"; }
  });
  const setSortBy = (v: SortBy) => { setSortByState(v); try { localStorage.setItem("todo-sort", v); } catch { /* private mode */ } };
  const notes = (data.lists.find((l) => l.id === data.activeListId) || data.lists[0])?.kind === "notes";
  const showAll = showDone || notes; // a list of things to remember hides nothing
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [addingSubFor, setAddingSubFor] = useState<string | null>(null);
  const [subText, setSubText] = useState("");
  const [editingListId, setEditingListId] = useState<string | null>(null);
  const [listName, setListName] = useState("");
  const [dragging, setDragging] = useState<string | null>(null); // task being dragged
  const [dropOn, setDropOn] = useState<string | null>(null);     // list it hovers over
  const [dropAt, setDropAt] = useState<DropAt | null>(null);       // row it hovers over
  const [showArchive, setShowArchive] = useState(false);
  const [sharing, setSharing] = useState(false); // share panel open
  const [shareEmail, setShareEmail] = useState("");
  const synced = useRef(new Map<string, string>()); // shared list id -> what its doc holds

  useEffect(() => {
    let cancelled = false;
    db.collection("users").doc(uid).get().then((snap) => {
      if (cancelled) return;
      setData(normalizeData(snap.data()?.todos));
      setHydrated(true);
    }).catch(() => { setData(normalizeData(null)); setHydrated(true); });
    return () => { cancelled = true; };
  }, [uid]);

  // Shared lists: the ones you own, and the ones shared with your email, live
  useEffect(() => {
    if (!hydrated) return;
    const col = db.collection("todoLists");
    let own: Remote | null = null;
    let theirs: Remote | null = null;
    const listen = (q: firebase.firestore.Query, mine: boolean) => q.onSnapshot((snap) => {
      const m: Remote = new Map(snap.docs.map((doc) => [doc.id, listFromDoc(doc.id, doc.data())]));
      const changed = new Set<string>();
      snap.docChanges().forEach((c) => {
        if (c.type === "removed") { synced.current.delete(c.doc.id); return; }
        changed.add(c.doc.id);
        synced.current.set(c.doc.id, sharedKey(m.get(c.doc.id)!));
      });
      if (mine) own = m; else theirs = m;
      setData((d) => mergeRemote(d, uid, own, theirs, changed));
    }, () => {});
    const stopOwn = listen(col.where("uid", "==", uid), true);
    const stopTheirs = email ? listen(col.where("sharedWith", "array-contains", email), false) : () => {};
    return () => { stopOwn(); stopTheirs(); };
  }, [hydrated, uid, email]);

  // Private lists go in your users doc; each shared list that changed is
  // written to its own todoLists doc
  useEffect(() => {
    if (!hydrated) return;
    const t = window.setTimeout(() => {
      const todos = {
        lists: data.lists.filter((l) => !l.shared).map(({ shared, ...l }) => l),
        activeListId: data.activeListId,
        templates: data.templates,
        order: data.lists.map((l) => l.id),
      };
      db.collection("users").doc(uid).set({ todos }, { merge: true }).catch(() => {});
      data.lists.forEach((l) => {
        if (!l.shared) return;
        const key = sharedKey(l);
        if (synced.current.get(l.id) === key) return;
        synced.current.set(l.id, key);
        db.collection("todoLists").doc(l.id).update(sharedFields(l)).catch(() => synced.current.delete(l.id));
      });
    }, 500);
    return () => window.clearTimeout(t);
  }, [data, hydrated, uid]);

  const active = data.lists.find((l) => l.id === data.activeListId) || data.lists[0];

  // ── list ops ──
  const updateActiveTasks = (fn: (tasks: Task[]) => Task[]) =>
    setData((d) => ({ ...d, lists: d.lists.map((l) => (l.id === (active?.id) ? { ...l, tasks: fn(l.tasks) } : l)) }));

  const addList = (kind: ListKind = "tasks") => {
    const [name, icon] = NEW_LIST[kind];
    const l = blankList(name, icon || ICONS[data.lists.length % ICONS.length], kind);
    setData((d) => ({ ...d, lists: [...d.lists, l], activeListId: l.id }));
    setEditingListId(l.id); setListName(l.name);
    setPicking(false);
  };
  const addFromTemplate = (t: Template) => {
    const l = listFromTemplate(t);
    setData((d) => ({ ...d, lists: [...d.lists, l], activeListId: l.id }));
    setEditingListId(l.id); setListName(l.name);
    setPicking(false);
  };
  const saveTemplate = (l: TodoList) => {
    setData((d) => ({ ...d, templates: [...d.templates.filter((t) => t.name !== l.name), templateFromList(l)] }));
    window.alert(`"${l.name}" är sparad som mall. Välj den under "＋ Ny lista".`);
  };
  const deleteTemplate = (id: string) => {
    if (!window.confirm("Ta bort mallen?")) return;
    setData((d) => ({ ...d, templates: d.templates.filter((t) => t.id !== id) }));
  };
  const setKind = (kind: ListKind) => {
    // Done tasks count as packed in a packing list, and the other way round
    const sync = (ts: Task[]): Task[] => ts.map((t) => ({ ...(t.group ? t : withPacked(t, t.status === "done" ? t.qty : 0)), children: sync(t.children) }));
    setData((d) => ({ ...d, lists: d.lists.map((l) => (l.id === active?.id && l.kind !== kind ? { ...l, kind, tasks: kind === "pack" ? sync(l.tasks) : l.tasks } : l)) }));
  };
  const unpackAll = () => {
    if (!window.confirm("Nollställ alla packade saker i listan?")) return;
    const reset = (ts: Task[]): Task[] => ts.map((t) => ({ ...(t.group ? t : withPacked(t, 0)), children: reset(t.children) }));
    updateActiveTasks(reset);
  };
  const selectList = (id: string) => setData((d) => ({ ...d, activeListId: id }));
  const startEditList = (l: TodoList) => { setEditingListId(l.id); setListName(l.name); };
  const commitEditList = () => {
    const v = listName.trim();
    if (v) setData((d) => ({ ...d, lists: d.lists.map((l) => (l.id === editingListId ? { ...l, name: v } : l)) }));
    setEditingListId(null); setListName("");
  };
  const cycleListIcon = (id: string) =>
    setData((d) => ({ ...d, lists: d.lists.map((l) => (l.id === id ? { ...l, icon: ICONS[(ICONS.indexOf(l.icon) + 1) % ICONS.length] } : l)) }));
  const shelf = data.lists.filter((l) => !l.archived);
  const archive = data.lists.filter((l) => l.archived);
  const setArchived = (id: string, archived: boolean) =>
    setData((d) => {
      const lists = d.lists.map((l) => (l.id === id ? { ...l, archived } : l));
      // Archiving the open list opens the first one still on the shelf
      const activeListId = archived && d.activeListId === id ? lists.find((l) => !l.archived)!.id : d.activeListId;
      return { ...d, lists, activeListId };
    });
  const moveTo = (taskId: string, listId: string) => setData((d) => moveTask(d, taskId, listId));

  const deleteList = async (id: string) => {
    const l = data.lists.find((x) => x.id === id);
    if (!l || data.lists.length <= 1) return;
    if (l.shared && l.shared.owner !== uid) return leaveList(l);
    const others = l.shared?.sharedWith.length ? " Den tas bort för alla den är delad med." : "";
    if (!window.confirm(`Ta bort listan och alla dess uppgifter?${others}`)) return;
    if (l.shared) {
      try { await db.collection("todoLists").doc(id).delete(); } catch { window.alert("Kunde inte ta bort listan just nu."); return; }
      synced.current.delete(id);
    }
    dropList(id);
  };

  // ── sharing ──
  const setSharedWith = (id: string, sharedWith: string[]) =>
    setData((d) => ({ ...d, lists: d.lists.map((l) => (l.id === id && l.shared ? { ...l, shared: { ...l.shared, sharedWith } } : l)) }));
  const shareWith = async (l: TodoList) => {
    const e = normEmail(shareEmail);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) { window.alert("Skriv en hel e-postadress."); return; }
    if (e === email) { window.alert("Det är din egen adress."); return; }
    setShareEmail("");
    if (l.shared) {
      if (!l.shared.sharedWith.includes(e)) setSharedWith(l.id, [...l.shared.sharedWith, e]);
      return;
    }
    // First share: the list moves to its own doc. It only leaves your users doc
    // once that doc is written, so nothing is lost if this fails.
    const s: Sharing = { owner: uid, ownerEmail: email, sharedWith: [e] };
    const next = { ...l, shared: s };
    try {
      await db.collection("todoLists").doc(l.id).set({ uid, ownerEmail: email, ...sharedFields(next) });
    } catch {
      window.alert("Kunde inte dela listan just nu. Försök igen.");
      return;
    }
    synced.current.set(l.id, sharedKey(next));
    setData((d) => ({ ...d, lists: d.lists.map((x) => (x.id === l.id ? { ...x, shared: s } : x)) }));
  };
  const dropList = (id: string) =>
    setData((d) => {
      const lists = d.lists.filter((l) => l.id !== id);
      if (!lists.length) lists.push(blankList("Att göra", "✅"));
      const open = lists.find((l) => !l.archived) ?? lists[0];
      return { ...d, lists, activeListId: d.activeListId === id ? open.id : d.activeListId };
    });
  const leaveList = async (l: TodoList) => {
    if (!window.confirm(`Lämna "${l.name}"? Den försvinner från dina listor, men ${l.shared?.ownerEmail || "ägaren"} har kvar den.`)) return;
    try {
      await db.collection("todoLists").doc(l.id).update({ sharedWith: firebase.firestore.FieldValue.arrayRemove(email) });
    } catch {
      window.alert("Kunde inte lämna listan just nu. Försök igen.");
      return;
    }
    synced.current.delete(l.id);
    setSharing(false);
    dropList(l.id);
  };

  // ── task ops ──
  const groups = active?.kind === "pack" ? active.tasks.filter((t) => t.group) : [];
  const addGroup = () => {
    const g = blankGroup();
    updateActiveTasks((ts) => [...ts, g]);
    setEditingId(g.id); setEditText(g.text);
  };
  const addTask = () => {
    const t = text.trim();
    if (!t || !active) return;
    if (active.kind === "pack") {
      const it = { ...blankTask(t), qty: Math.max(1, qty || 1) };
      updateActiveTasks((tasks) => (groups.some((g) => g.id === addTo) ? addChild(tasks, addTo, it) : [...tasks, it]));
    } else if (active.kind === "notes") {
      updateActiveTasks((tasks) => [...tasks, blankTask(t)]);
    } else {
      updateActiveTasks((tasks) => [...tasks, { ...blankTask(t), due, priority }]);
    }
    setText(""); setDue(""); setPriority("normal"); setQty(1);
  };

  const handlers: Handlers = {
    cycle: (id) => updateActiveTasks((ts) => mapTask(ts, id, (t) => ({ ...t, status: nextStatus(t.status) }))),
    setStatus: (id, s) => updateActiveTasks((ts) => mapTask(ts, id, (t) => ({ ...t, status: s }))),
    setPrio: (id, p) => updateActiveTasks((ts) => mapTask(ts, id, (t) => ({ ...t, priority: p }))),
    setDue: (id, d) => updateActiveTasks((ts) => mapTask(ts, id, (t) => ({ ...t, due: d }))),
    del: (id) => updateActiveTasks((ts) => removeTask(ts, id)),
    editingId, editText, setEditText,
    startEdit: (t) => { setEditingId(t.id); setEditText(t.text); },
    commitEdit: () => {
      const v = editText.trim();
      updateActiveTasks((ts) => (v ? mapTask(ts, editingId!, (t) => ({ ...t, text: v })) : removeTask(ts, editingId!)));
      setEditingId(null); setEditText("");
    },
    cancelEdit: () => { setEditingId(null); setEditText(""); },
    addingSubFor, subText, setSubText,
    openAddSub: (id) => { setAddingSubFor(id); setSubText(""); },
    commitAddSub: () => {
      const v = subText.trim();
      if (v) updateActiveTasks((ts) => addChild(ts, addingSubFor!, blankTask(v)));
      setAddingSubFor(null); setSubText("");
    },
    cancelAddSub: () => { setAddingSubFor(null); setSubText(""); },
    moveTargets: shelf.filter((l) => l.id !== active?.id),
    move: moveTo,
    setDragging: (id) => { setDragging(id); if (!id) { setDropOn(null); setDropAt(null); } },
    dragging, dropAt, setDropAt,
    draggingGroup: !!(dragging && active && findTask(active.tasks, dragging)?.group),
    place: (to) => {
      if (dragging) updateActiveTasks((ts) => (to.inside ? nestTask(ts, dragging, to.id) : placeTask(ts, dragging, to.id, to.after)));
    },
    groups,
    nest: (id, groupId) => updateActiveTasks((ts) => nestTask(ts, id, groupId)),
    packGroup: (id, on) => updateActiveTasks((ts) => mapTask(ts, id, (t) => packAll(t, on))),
    sumOf: (id) => {
      const items = packItems(findTask(active?.tasks ?? [], id)?.children ?? []);
      return { items: items.length, packed: items.filter((t) => t.packed >= t.qty).length };
    },
    // Hidden rows are stepped over so every press visibly moves the row
    shift: (id, by) => updateActiveTasks((ts) => shiftTask(ts, id, by, (t) => !showAll && (t.status === "done" || t.status === "ignored"))),
    setQty: (id, n) => updateActiveTasks((ts) => mapTask(ts, id, (t) => withPacked({ ...t, qty: Math.max(1, Math.min(99, Math.round(n) || 1)) }, t.packed))),
    setPacked: (id, n) => updateActiveTasks((ts) => mapTask(ts, id, (t) => withPacked(t, n))),
    setNote: (id, note) => updateActiveTasks((ts) => mapTask(ts, id, (t) => ({ ...t, note }))),
    openId, setOpenId,
    sorted: active?.kind === "tasks" && sortBy !== "own",
  };

  const visible = useMemo(() => (active ? sortTasks(filterTasks(active.tasks, showAll), active.kind === "tasks" ? sortBy : "own") : []), [active, showAll, sortBy]);
  const activeOpen = active ? openCount(active.tasks) : 0;
  const soon = useMemo(() => dueSummary(active?.kind === "tasks" ? active.tasks : []), [active]);
  const pack = active?.kind === "pack";
  const packTotal = useMemo(() => {
    const sum = { qty: 0, packed: 0 };
    packItems(active?.tasks ?? []).forEach((t) => {
      sum.qty += t.qty;
      sum.packed += Math.min(t.packed, t.qty);
    });
    return sum;
  }, [active]);

  return (
    <div className="todo">
      <div className="todo__header">
        <NavLink to={routeUrls.PROJEKT} className="todo__back">← Projekt</NavLink>
        <h1 className="todo__title">Att göra</h1>
        <span className="todo__sub">Flera listor, deluppgifter och statusar — sparas automatiskt</span>
      </div>

      <div className="todo__layout">
        {/* sidebar */}
        <aside className="todo__sidebar">
          {shelf.map((l) => (
            <div
              key={l.id}
              className={`todo__listrow ${l.id === active?.id ? "todo__listrow--on" : ""} ${dropOn === l.id ? "todo__listrow--drop" : ""}`}
              onDragOver={(e) => {
                if (!dragging || l.id === active?.id) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                setDropOn(l.id);
                setDropAt(null);
              }}
              onDragLeave={() => setDropOn((x) => (x === l.id ? null : x))}
              onDrop={(e) => {
                e.preventDefault();
                if (dragging) moveTo(dragging, l.id);
                setDragging(null);
                setDropOn(null);
              }}
            >
              <button className="todo__list-icon" onClick={() => cycleListIcon(l.id)} title="Byt ikon">{l.icon}</button>
              {editingListId === l.id ? (
                <input
                  autoFocus className="todo__list-edit" value={listName}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => setListName(e.target.value)} onBlur={commitEditList}
                  onKeyDown={(e) => { if (e.key === "Enter") commitEditList(); if (e.key === "Escape") { setEditingListId(null); setListName(""); } }}
                />
              ) : (
                <button className="todo__list-name" onClick={() => selectList(l.id)} onDoubleClick={() => startEditList(l)}>
                  {l.name}
                  {isShared(l, uid) && <span className="todo__list-shared" title={l.shared!.owner === uid ? "Du delar listan" : `Delad av ${l.shared!.ownerEmail}`} aria-label="Delad">🤝</span>}
                  {l.kind !== "notes" && openCount(l.tasks) > 0 && <span className="todo__list-badge">{openCount(l.tasks)}</span>}
                </button>
              )}
              {shelf.length > 1 && <button className="todo__list-arch" onClick={() => setArchived(l.id, true)} title="Arkivera lista" aria-label="Arkivera lista">⤓</button>}
            </div>
          ))}
          <button className="todo__addlist" onClick={() => setPicking((v) => !v)} aria-expanded={picking}>＋ Ny lista</button>
          {picking && (
            <div className="todo__picker">
              <button onClick={() => addList("tasks")}>📝 Tom lista</button>
              <button onClick={() => addList("pack")}>🧳 Tom packlista</button>
              <button onClick={() => addList("notes")}>📌 Tom minneslista <small>serier, böcker, tips…</small></button>
              <span className="todo__picker-label">Mallar</span>
              {[...BUILTIN_TEMPLATES, ...data.templates].map((t) => (
                <div key={t.id} className="todo__picker-row">
                  <button onClick={() => addFromTemplate(t)}>{t.icon} {t.name} {t.tasks.length > 0 && <small>{t.kind === "pack" ? packItems(t.tasks).length : t.tasks.length} st</small>}</button>
                  {!t.id.startsWith("builtin") && <button className="todo__list-del" onClick={() => deleteTemplate(t.id)} aria-label={`Ta bort mallen ${t.name}`}>×</button>}
                </div>
              ))}
            </div>
          )}
          {dragging && <p className="todo__drophint">Släpp på en rad för att flytta den dit, eller på en lista</p>}
          {archive.length > 0 && (
            <>
              <button className="todo__archbtn" onClick={() => setShowArchive((v) => !v)} aria-expanded={showArchive}>
                {showArchive ? "▾" : "▸"} Arkiv ({archive.length})
              </button>
              {showArchive && archive.map((l) => (
                <div key={l.id} className={`todo__listrow todo__listrow--arch ${l.id === active?.id ? "todo__listrow--on" : ""}`}>
                  <span className="todo__list-icon">{l.icon}</span>
                  <button className="todo__list-name" onClick={() => selectList(l.id)}>{l.name}</button>
                  <button className="todo__list-arch" onClick={() => setArchived(l.id, false)} title="Återställ lista" aria-label="Återställ lista">⤒</button>
                  <button className="todo__list-del" onClick={() => deleteList(l.id)} aria-label="Ta bort lista">×</button>
                </div>
              ))}
            </>
          )}
        </aside>

        {/* main */}
        <div className="todo__main">
          {active?.archived && (
            <div className="todo__archnote">
              <span>{active.icon} {active.name} är arkiverad.</span>
              <button onClick={() => setArchived(active.id, false)}>Återställ</button>
            </div>
          )}
          <div className="todo__add">
            <input
              className="todo__add-text"
              placeholder={active ? `Lägg till i "${active.name}"…` : "Lägg till…"}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addTask(); }}
            />
            {active?.kind === "pack" ? (
              <>
              <label className="todo__add-qty" title="Antal">
                <NumberInput min={1} max={99} integer inputMode="numeric" value={qty} onChange={setQty} onKeyDown={(e) => { if (e.key === "Enter") addTask(); }} />
                st
              </label>
              {groups.length > 0 && (
                <select className="todo__add-group" value={groups.some((g) => g.id === addTo) ? addTo : ""} onChange={(e) => setAddTo(e.target.value)} title="Grupp" aria-label="Lägg i grupp">
                  <option value="">Ingen grupp</option>
                  {groups.map((g) => <option key={g.id} value={g.id}>{g.text}</option>)}
                </select>
              )}
              </>
            ) : active?.kind === "notes" ? null : (
              <>
                <input className="todo__add-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} title="Deadline (valfritt)" />
                <select className="todo__add-prio" value={priority} onChange={(e) => setPriority(e.target.value as Priority)} title="Prioritet">
                  <option value="high">Hög</option>
                  <option value="normal">Normal</option>
                  <option value="low">Låg</option>
                </select>
              </>
            )}
            <button className="todo__add-btn" onClick={addTask} disabled={!text.trim()}>Lägg till</button>
          </div>

          {active && (
            <div className="todo__listtools">
              <span className="todo__kind" role="group" aria-label="Format">
                {(["tasks", "pack", "notes"] as ListKind[]).map((k) => (
                  <button key={k} aria-pressed={active.kind === k} onClick={() => setKind(k)}>{KIND_LABEL[k]}</button>
                ))}
              </span>
              <button className="todo__tool" onClick={() => saveTemplate(active)} disabled={!active.tasks.length}>Spara som mall</button>
              <button className="todo__tool" onClick={() => setSharing((v) => !v)} aria-expanded={sharing}>
                🤝 {isShared(active, uid) ? "Delad" : "Dela"}
              </button>
              {pack && <button className="todo__tool" onClick={addGroup}>＋ Ny grupp</button>}
              {pack && packTotal.packed > 0 && <button className="todo__tool" onClick={unpackAll}>Nollställ packat</button>}
            </div>
          )}

          {active && sharing && (
            <div className="todo__share">
              {active.shared && active.shared.owner !== uid ? (
                <>
                  <p>Delad med dig av <b>{active.shared.ownerEmail}</b>. Ni ser och ändrar samma lista.</p>
                  {active.shared.sharedWith.length > 1 && <p className="todo__share-who">Även: {active.shared.sharedWith.filter((e) => e !== email).join(", ")}</p>}
                  <button className="todo__tool" onClick={() => leaveList(active)}>Lämna listan</button>
                </>
              ) : (
                <>
                  <p>Dela listan med någon som har ett konto här. Ni ser och ändrar samma lista, och ändringar syns direkt.</p>
                  <div className="todo__share-add">
                    <input
                      type="email" inputMode="email" autoComplete="off" placeholder="namn@exempel.se" aria-label="E-postadress"
                      value={shareEmail} onChange={(e) => setShareEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") shareWith(active); }}
                    />
                    <button className="todo__tool" onClick={() => shareWith(active)} disabled={!shareEmail.trim()}>Dela</button>
                  </div>
                  {active.shared && active.shared.sharedWith.length > 0 && (
                    <ul className="todo__share-list">
                      {active.shared.sharedWith.map((e) => (
                        <li key={e}>
                          {e}
                          <button className="todo__del" onClick={() => setSharedWith(active.id, active.shared!.sharedWith.filter((x) => x !== e))} aria-label={`Sluta dela med ${e}`}>×</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}

          <div className="todo__toolbar">
            {pack ? (
              <span className="todo__count todo__packsum">
                {packTotal.packed} av {packTotal.qty} packade
                <span className="todo__prog-bar"><span className="todo__prog-fill" style={{ width: `${packTotal.qty ? Math.round((packTotal.packed / packTotal.qty) * 100) : 0}%` }} /></span>
              </span>
            ) : notes ? (
              <span className="todo__count">{active!.tasks.length} {active!.tasks.length === 1 ? "sak" : "saker"}</span>
            ) : (
              <span className="todo__count">
                {activeOpen} kvar
                {soon.overdue > 0 && <span className="todo__due todo__due--overdue">{soon.overdue} försenad{soon.overdue > 1 ? "e" : ""}</span>}
                {soon.today > 0 && <span className="todo__due todo__due--today">{soon.today} idag</span>}
                {soon.week > 0 && <span className="todo__due">{soon.week} inom en vecka</span>}
              </span>
            )}
            {!pack && !notes && (
              <select className="todo__sort" value={sortBy} onChange={(e) => setSortBy(e.target.value as SortBy)} aria-label="Sortera">
                <option value="own">Egen ordning</option>
                <option value="due">Efter deadline</option>
                <option value="prio">Efter prioritet</option>
              </select>
            )}
            {!notes && (
              <label className="todo__toggle">
                <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
                {pack ? "Visa packade" : "Visa klara/ignorerade"}
              </label>
            )}
          </div>

          <ul className="todo__list">
            {visible.length === 0 && (
              <li className="todo__empty">
                {active && active.tasks.length
                  ? (pack ? "Allt är packat! 🎉" : "Inget att visa — allt är klart! 🎉")
                  : notes ? "Inget här ännu — lägg till något du vill komma ihåg, som en serie eller en bok."
                  : (pack ? "Inget att packa ännu — lägg till saker ovan." : "Ingen uppgift ännu — lägg till en ovan.")}
              </li>
            )}
            {visible.map((t) => (notes ? <NoteNode key={t.id} task={t} h={handlers} />
              : !pack ? <TaskNode key={t.id} task={t} depth={0} h={handlers} />
              : t.group || t.children.length ? <PackGroup key={t.id} task={t} h={handlers} />
              : <PackNode key={t.id} task={t} h={handlers} />))}
          </ul>
        </div>
      </div>
    </div>
  );
};
